<template>
  <main class="about-us-container" role="main" aria-labelledby="about-title">
    <div class="header-container">

      <div class="text-section">
        <h1 class="about-title" id="about-title">
          The PortAL Linking <br />
          <span class="subtitle-line">Artists and Scientists</span>
        </h1>

        <!-- About paragraph - editable by admin -->
        <div v-if="editingAbout && isAdmin" class="edit-section">
          <textarea
            v-model="aboutParagraph"
            class="edit-textarea"
            rows="6"
          ></textarea>
          <div class="edit-actions">
            <button @click="saveAbout" class="save-btn">Save</button>
            <button @click="cancelEditAbout" class="cancel-btn">Cancel</button>
          </div>
        </div>
        <div v-else class="about-par-wrapper">
          <p class="about-par">{{ aboutParagraph }}</p>
          <button v-if="isAdmin" @click="startEditAbout" class="edit-icon-btn" title="Edit about text">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        </div>

        <div class="button-row">
          <button
            @click="openJoinModal"
            @keydown.enter="openJoinModal"
            class="contact-us-btn"
            tabindex="0"
            aria-label="Join the PALLAS community"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <path d="M20 8v6M23 11h-6"/>
            </svg>
            <span>Join PALLAS</span>
          </button>
          <button
            @click="openContactModal"
            @keydown.enter="openContactModal"
            class="contact-us-btn"
            tabindex="0"
            aria-label="Contact the PALLAS team via email"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span>Contact Us</span>
          </button>
        </div>
      </div>
    </div>

    <div class="public-section">
      <div class="whole-thing">

      <!-- Team section header - editable by admin -->
      <div v-if="editingTeamSection && isAdmin" class="edit-section">
        <input v-model="teamSectionTitle" class="edit-input" placeholder="Section Title" />
        <textarea
          v-model="teamSectionText"
          class="edit-textarea"
          rows="3"
        ></textarea>
        <div class="edit-actions">
          <button @click="saveTeamSection" class="save-btn">Save</button>
          <button @click="cancelEditTeamSection" class="cancel-btn">Cancel</button>
        </div>
      </div>
      <div v-else>
        <div class="section-header-wrapper">
          <h2 class="team-title" id="team-section">{{ teamSectionTitle }}</h2>
          <button v-if="isAdmin" @click="startEditTeamSection" class="edit-icon-btn" title="Edit section">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        </div>
        <p class="team-text" role="doc-subtitle" aria-describedby="team-section">{{ teamSectionText }}</p>
      </div>

      <!-- Add team member button for admin -->
      <div v-if="isAdmin" class="admin-actions">
        <button @click="addTeamMember" class="add-btn">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          New Team Member
        </button>
      </div>

      <ul class="team-list" role="list" aria-label="PALLAS team members">
        <li
          v-for="(member, index) in sortedTeamMembers"
          :key="member._uid || index"
          class="team-member-container"
          role="listitem"
        >
          <!-- Edit form for team member -->
          <div v-if="member.editing && isAdmin" class="member-edit-form">
            <input v-model="member.name" class="edit-input" placeholder="Name" />
            <input v-model="member.role" class="edit-input" placeholder="Role" />
            <textarea v-model="member.bio" class="edit-textarea" rows="4" placeholder="Bio"></textarea>
            <div class="image-upload-section">
              <button @click="openImageUploadModal(member, 'member')" class="upload-btn" type="button">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload Image
              </button>
            </div>
            <div v-if="member.imageUrl" class="image-preview">
              <img :src="member.imageUrl" alt="Preview" />
            </div>
            <div class="edit-actions">
              <button @click="saveTeamMember(member)" class="save-btn">Save</button>
              <button @click="cancelEditTeamMember(member)" class="cancel-btn">Cancel</button>
            </div>
          </div>

          <!-- Normal card display -->
          <div v-else class="card-wrapper">
            <div
              class="card"
              tabindex="0"
              role="button"
              :aria-label="`View bio for ${member.name}, ${member.role}`"
              @click="toggleCard(index)"
              @keydown.enter="toggleCard(index)"
              @keydown.space.prevent="toggleCard(index)"
              :data-flipped="flippedCards[index]"
            >
              <div class="card-inner" :style="{ backgroundImage: `url(${member.imageUrl || member.image})` }">
                <div class="card-front">
                  <div class="card-info">
                    <p class="title" role="heading" aria-level="3">{{ member.name }}</p>
                    <p class="subtitle">{{ member.role }}</p>
                  </div>
                </div>
                <div class="card-back">
                  <div class="hover-content" role="region" :aria-label="`Biography of ${member.name}`">
                    <p v-html="formatBio(member.bio)" :id="`bio-${index}`"></p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Admin edit/delete buttons -->
            <div v-if="isAdmin" class="card-admin-actions">
              <button @click="editTeamMember(member)" class="card-edit-btn" title="Edit member">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button @click="removeTeamMember(member)" class="card-delete-btn" title="Remove member">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>

    <div class="pet-section">
      <div class="whole-thing">

      <!-- Pets section header - editable by admin -->
      <div v-if="editingPetsSection && isAdmin" class="edit-section">
        <input v-model="petsSectionTitle" class="edit-input" placeholder="Section Title" />
        <textarea
          v-model="petsSectionText"
          class="edit-textarea"
          rows="2"
        ></textarea>
        <div class="edit-actions">
          <button @click="savePetsSection" class="save-btn">Save</button>
          <button @click="cancelEditPetsSection" class="cancel-btn">Cancel</button>
        </div>
      </div>
      <div v-else>
        <div class="section-header-wrapper">
          <h2 class="team-title" id="pets-section">{{ petsSectionTitle }}</h2>
          <button v-if="isAdmin" @click="startEditPetsSection" class="edit-icon-btn" title="Edit section">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        </div>
        <p class="team-text" role="doc-subtitle" aria-describedby="pets-section">{{ petsSectionText }}</p>
      </div>

      <!-- Add pet button for admin -->
      <div v-if="isAdmin" class="admin-actions">
        <button @click="addPet" class="add-btn">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          New Pet
        </button>
      </div>

      <ul class="team-list" role="list" aria-label="PALLAS pets">
        <li
          v-for="(member, index) in teamCats"
          :key="member._uid || `cat-${index}`"
          class="team-member-container"
          role="listitem"
        >
          <!-- Edit form for pet -->
          <div v-if="member.editing && isAdmin" class="member-edit-form">
            <input v-model="member.name" class="edit-input" placeholder="Name" />
            <input v-model="member.role" class="edit-input" placeholder="Role/Title" />
            <textarea v-model="member.bio" class="edit-textarea" rows="3" placeholder="Bio"></textarea>
            <div class="image-upload-section">
              <button @click="openImageUploadModal(member, 'pet')" class="upload-btn" type="button">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload Image
              </button>
            </div>
            <div v-if="member.imageUrl" class="image-preview">
              <img :src="member.imageUrl" alt="Preview" />
            </div>
            <div class="edit-actions">
              <button @click="savePet(member)" class="save-btn">Save</button>
              <button @click="cancelEditPet(member)" class="cancel-btn">Cancel</button>
            </div>
          </div>

          <!-- Normal card display -->
          <div v-else class="card-wrapper">
            <div
              class="card"
              tabindex="0"
              role="button"
              :aria-label="`View bio for ${member.name}, ${member.role}`"
              @click="toggleCatCard(index)"
              @keydown.enter="toggleCatCard(index)"
              @keydown.space.prevent="toggleCatCard(index)"
              :data-flipped="flippedCatCards[index]"
            >
              <div class="card-inner" :style="{ backgroundImage: `url(${member.imageUrl || member.image})` }">
                <div class="card-front">
                  <div class="card-info">
                    <p class="title" role="heading" aria-level="3">{{ member.name }}</p>
                    <p class="subtitle">{{ member.role }}</p>
                  </div>
                </div>
                <div class="card-back">
                  <div class="hover-content" role="region" :aria-label="`Biography of ${member.name}`">
                    <p v-html="formatBio(member.bio)" :id="`cat-bio-${index}`"></p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Admin edit/delete buttons -->
            <div v-if="isAdmin" class="card-admin-actions">
              <button @click="editPet(member)" class="card-edit-btn" title="Edit pet">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button @click="removePet(member)" class="card-delete-btn" title="Remove pet">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
    </div>

    <ContactModal />

    <!-- Image Upload Modal -->
    <ImageUploadModal
      v-if="showImageUploadModal"
      :title="currentEditingItem?.name ? `Image for ${currentEditingItem.name}` : 'Upload Image'"
      :currentImage="currentEditingItem?.imageUrl || ''"
      imageType="profile"
      accentColor="blend"
      @upload="handleImageUploadComplete"
      @close="closeImageUploadModal"
    />
  </main>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { openContactForm } from '../utils/eventBus';
import { openSignUpModal } from '../composables/useAuthModal';
import ContactModal from './ContactModal.vue';
import ImageUploadModal from './ImageUploadModal.vue';
import { db } from '../firebase';
import { collection, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';

export default {
  name: "AboutUs",
  components: {
    ContactModal,
    ImageUploadModal
  },
  setup() {
    const flippedCards = ref({});
    const flippedCatCards = ref({});

    // Admin state
    const isAdmin = computed(() => {
      try {
        const userInfo = localStorage.getItem('user-info');
        if (userInfo) {
          const parsed = JSON.parse(userInfo);
          return parsed.adminRole === 'admin' || parsed.role === 'admin';
        }
        return false;
      } catch {
        return false;
      }
    });

    // Editable content
    const aboutParagraph = ref("At PALLAS, we are passionate about integrating artists into the fabric of the scientific ecosystem. Our goals are to facilitate interactions between artists and scientists and advocate for increased inclusivity of art in scientific research and communication. By providing an easy way to connect artists with scientists, we hope to support lasting collaborations that lead to a more creative and vibrant environment at the intersection of art and science.");
    const teamSectionTitle = ref("Meet the PALLAS Team");
    const teamSectionText = ref("PALLAS was founded by the Pallasites*\u2014seven scientists who wish they had better art skills. Learn more about us below. *Pallasites are also one of the most rad (and expensive) kind of meteorite. We accept donations.");
    const petsSectionTitle = ref("PALLAS Pets");
    const petsSectionText = ref("The true masterminds of our team... meet the PALLAS Pets!");

    // Editing states
    const editingAbout = ref(false);
    const editingTeamSection = ref(false);
    const editingPetsSection = ref(false);
    const editingMemberId = ref(null);
    const editingPetId = ref(null);
    const uploadingImage = ref(false);

    // Image upload modal state
    const showImageUploadModal = ref(false);
    const currentEditingItem = ref(null);
    const currentItemType = ref(null); // 'member' or 'pet'

    // Helper to get last name from full name
    const getLastName = (name) => {
      if (!name) return '';
      const parts = name.trim().split(' ');
      return parts[parts.length - 1].toLowerCase();
    };

    // Sorted team members by last name
    const sortedTeamMembers = computed(() => {
      return [...teamMembers.value].sort((a, b) => {
        return getLastName(a.name).localeCompare(getLastName(b.name));
      });
    });

    const teamMembers = ref([
      {
        name: "Ashika Capirala",
        role: "Lead Cat",
        bio: "I’m a 5th year PhD candidate at Purdue University. I use computer models to study the ocean and marine biosphere to bridge the evolution of Earth’s oceans to those on other worlds. In my spare time, I love doing graphic design, photomanipulation, and digital painting to bring alien worlds to life.",
        image: require('../assets/images/ash.jpg'),
      },
      {
        name: "Lucas Fifer",
        role: "Lead Cat",
        bio: "I'm a postdoctoral researcher at the Earth-Life Science Institute at the Institute of Science Tokyo. I study the potential for life on icy moons in our solar system, for example, what eruptions on Enceladus can tell us about the chemistry and habitability of its subsurface ocean. I've always enjoyed drawing for fun (but I'm not very good), and have recently gotten into making pixel art.",
        image: require('../assets/images/lucus.jpg'),
      },
      {
        name: "Émilie A. Laflèche",
        role: "Lead Cat",
        bio: "I’m a PhD candidate in Planetary Science at Purdue University. My research explores the effects of seasons on the emergence of complex life on Earth, as well as how extreme seasons may impact exoplanet habitability and life detection! I’ve also always loved crafting, and in my free time I crochet/knit, bake for friends, and hang out with my cat Chai.",
        image: require('../assets/images/emilie.jpg'),
      },
      {
        name: "Douglas Nyberg",
        role: "Lead Cat",
        bio: "Douglas holds a BA in Physics with a minor in Computer Science and serves as the lead programmer for the PALLAS project. Beyond his work on PALLAS, he has developed autonomous agents that adapt to human trust to enhance team cohesion and has contributed to quantum supply chain optimization for intermittent demand forecasting for the Department of Defense.",
        image: require('../assets/images/douglas.jpg'),
      },
      {
        name: "Haleigh Nyberg",
        role: "Lead Cat",
        bio: "I am an NSF GRFP Fellow in Stephanie Olson’s PHaB lab at Purdue University. My research focuses on identifying factors that might encourage an origin of life on Earth-like exoplanets through climate modeling and machine learning techniques. I'm passionate about ceramics, 3D-modeling, and crocheting!",
        image: require('../assets/images/haleigh.jpg'),
      },
      {
        name: "Rebecca Payne",
        role: "Lead Cat",
        bio: "I'm an assistant professor in Earth & Climate Sciences and Physics & Astronomy at Bates College. I study the atmospheres of habitable planets, and am particularly interested in how the Earth's climate, atmospheric chemistry, and biosignatures have changed over time. In my spare time, I like to paint, crochet, bake, and continue my quest to try to learn every handicraft under the sun",
        image: require('../assets/images/becca.jpg'),
      },
      {
        name: "Evan Sneed",
        role: "Lead Cat",
        bio: "I'm a Ph.D. candidate in Earth and Planetary Sciences at UC Riverside, where I study exoplanet atmospheres, habitability, biosignatures, and the search for extraterrestrial intelligence (SETI). I'm especially interested in the connection between science and art as a way to make complex planetary ideas more visual, accessible, and memorable. Alongside my research, I enjoy photography and graphic design!",
        image: require('../assets/images/evan.png'),
      },
      {
        name: "Michael L. Wong",
        role: "Lead Cat",
        bio: "I'm a Research Scientist at Carnegie Science's Earth & Planets Laboratory. I'm interested in looking for life in the universe\u2014both within the solar system and on distant exoplanets\u2014through a blend of planetary science and data science. I love photography, typography, and podcasting!",
        image: require('../assets/images/mike.jpg'),
      },
    ]);

    const teamCats = ref([
      {
        name: "Chai Laflèche",
        role: "Diva Rat",
        bio: "I hate everything today.",
        image: require('../assets/images/chai.jpg'),
      },
      {
        name: "Muffin Nyberg",
        role: "Fluffy Rat",
        bio: "Is Fluffy Rat",
        image: require('../assets/images/muffin_new.jpg'),
      },
      {
        name: "Pearl Nyberg",
        role: "Zoomie Rat",
        bio: "Gotta Go Fast",
        image: require('../assets/images/pearl.jpg'),
      },
      {
        name: "Beignet Payne",
        role: "Beautiful Rat",
        bio: "Smol Rat",
        image: require('../assets/images/beignet.jpg'),
      },
      {
        name: "Tibbithy Nyberg",
        role: "Tree Rat",
        bio: "(Also known as Tobias Fate, Tibby, Tibbers, and Squirrel, Mini-Muffin, Mini Muff Muff, Pearl 2.0, and rat)",
        image: require('../assets/images/tib.jpg'),
      },
    ]);

    const openContactModal = () => {
      openContactForm();
    };

    const openJoinModal = () => {
      openSignUpModal('both');
    };

    const toggleCard = (index) => {
      flippedCards.value[index] = !flippedCards.value[index];
    };

    const toggleCatCard = (index) => {
      flippedCatCards.value[index] = !flippedCatCards.value[index];
    };

    const formatBio = (bio) => {
      // Much simpler approach - just check each word and highlight if it matches
      const words = bio.split(' ');

      const scienceWords = [
        'PhD', 'student', 'candidate', 'professor', 'Fellow', 'University', 'Science', 'Sciences',
        'Laboratory', 'Astrobiology', 'Planetary', 'computer', 'models', 'ocean', 'biosphere',
        'exoplanets', 'moons', 'solar', 'system', 'chemistry', 'habitability', 'climate',
        'modeling', 'machine', 'learning', 'atmospheres', 'planets', 'biosignatures',
        'research', 'physicist', 'astrobiologists', 'meteorite', 'seasons', 'emergence',
        'complex', 'life', 'detection', 'data', 'ancient', 'Earth', 'subsurface',
        'atmospheric', 'habitable', 'origin', 'Enceladus', 'Purdue', 'Washington',
        'Carnegie', 'Physics', 'Astronomy', 'NSF', 'GRFP', 'PHaB', 'lab'
      ];

      const artWords = [
        'graphic', 'design', 'photomanipulation', 'digital', 'painting', 'drawing',
        'pixel', 'art', 'crafting', 'crochet', 'knit', 'bake', 'ceramics',
        'paint', 'photography', 'typography', 'podcasting', 'handicraft', 'crocheting'
      ];

      const highlightedWords = words.map(word => {
        const cleanWord = word.replace(/[.,!?;:]/g, '');

        if (scienceWords.some(sciWord => cleanWord.toLowerCase() === sciWord.toLowerCase())) {
          return word.replace(cleanWord, `<span class="science-highlight">${cleanWord}</span>`);
        }

        if (artWords.some(artWord => cleanWord.toLowerCase() === artWord.toLowerCase())) {
          return word.replace(cleanWord, `<span class="art-highlight">${cleanWord}</span>`);
        }

        return word;
      });

      return highlightedWords.join(' ');
    };

    // Admin methods
    const startEditAbout = () => {
      editingAbout.value = true;
    };

    const saveAbout = async () => {
      try {
        await setDoc(doc(db, 'siteContent', 'about'), {
          aboutParagraph: aboutParagraph.value,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        editingAbout.value = false;
      } catch (error) {
        console.error('Error saving about content:', error);
      }
    };

    const cancelEditAbout = () => {
      editingAbout.value = false;
      loadContent();
    };

    const startEditTeamSection = () => {
      editingTeamSection.value = true;
    };

    const saveTeamSection = async () => {
      try {
        await setDoc(doc(db, 'siteContent', 'about'), {
          teamSectionTitle: teamSectionTitle.value,
          teamSectionText: teamSectionText.value,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        editingTeamSection.value = false;
      } catch (error) {
        console.error('Error saving team section:', error);
      }
    };

    const cancelEditTeamSection = () => {
      editingTeamSection.value = false;
      loadContent();
    };

    const startEditPetsSection = () => {
      editingPetsSection.value = true;
    };

    const savePetsSection = async () => {
      try {
        await setDoc(doc(db, 'siteContent', 'about'), {
          petsSectionTitle: petsSectionTitle.value,
          petsSectionText: petsSectionText.value,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        editingPetsSection.value = false;
      } catch (error) {
        console.error('Error saving pets section:', error);
      }
    };

    const cancelEditPetsSection = () => {
      editingPetsSection.value = false;
      loadContent();
    };

    // Team member methods
    const addTeamMember = () => {
      const newMember = {
        _uid: `member-${Date.now()}`,
        name: 'New Team Member',
        role: 'Role',
        bio: 'Enter bio here...',
        image: '',
        imageUrl: '',
        isNew: true,
        editing: true
      };
      teamMembers.value.unshift(newMember);
      editingMemberId.value = newMember._uid;
    };

    // Generic CRUD helpers for team members and pets
    const startEdit = (item, editingIdRef) => {
      item._originalName = item.name;
      item._originalRole = item.role;
      item._originalBio = item.bio;
      item._originalImageUrl = item.imageUrl;
      item.editing = true;
      editingIdRef.value = item._uid;
    };

    const saveItem = async (item, collectionName, prefix, editingIdRef) => {
      try {
        const data = {
          name: item.name,
          role: item.role,
          bio: item.bio,
          imageUrl: item.imageUrl || '',
          updatedAt: new Date().toISOString()
        };

        if (item._uid && !item._uid.startsWith(prefix) && !item.isNew) {
          // Existing Firestore doc — update
          await updateDoc(doc(db, collectionName, item._uid), data);
        } else {
          // New item or hardcoded default (no _uid or prefixed _uid) — create in Firestore
          const docRef = await addDoc(collection(db, collectionName), {
            ...data,
            createdAt: new Date().toISOString()
          });
          item._uid = docRef.id;
          delete item.isNew;
        }

        item.editing = false;
        editingIdRef.value = null;
        delete item._originalName;
        delete item._originalRole;
        delete item._originalBio;
        delete item._originalImageUrl;
      } catch (error) {
        console.error(`Error saving ${collectionName}:`, error);
      }
    };

    const cancelEdit = (item, arrayRef, editingIdRef) => {
      if (item.isNew) {
        const index = arrayRef.value.indexOf(item);
        if (index > -1) arrayRef.value.splice(index, 1);
      } else {
        item.name = item._originalName;
        item.role = item._originalRole;
        item.bio = item._originalBio;
        item.imageUrl = item._originalImageUrl;
        item.editing = false;
        delete item._originalName;
        delete item._originalRole;
        delete item._originalBio;
        delete item._originalImageUrl;
      }
      editingIdRef.value = null;
    };

    const removeItem = async (item, collectionName, prefix, arrayRef) => {
      if (!confirm(`Are you sure you want to remove ${item.name}?`)) return;
      try {
        if (item._uid && !item._uid.startsWith(prefix)) {
          await deleteDoc(doc(db, collectionName, item._uid));
        }
        const index = arrayRef.value.indexOf(item);
        if (index > -1) arrayRef.value.splice(index, 1);
      } catch (error) {
        console.error(`Error removing from ${collectionName}:`, error);
      }
    };

    // Team member methods
    const editTeamMember = (member) => startEdit(member, editingMemberId);
    const saveTeamMember = (member) => saveItem(member, 'teamMembers', 'member-', editingMemberId);
    const cancelEditTeamMember = (member) => cancelEdit(member, teamMembers, editingMemberId);
    const removeTeamMember = (member) => removeItem(member, 'teamMembers', 'member-', teamMembers);

    // Pet methods
    const addPet = () => {
      const newPet = {
        _uid: `pet-${Date.now()}`,
        name: 'New Pet',
        role: 'Pet Role',
        bio: 'Enter bio here...',
        image: '',
        imageUrl: '',
        isNew: true,
        editing: true
      };
      teamCats.value.unshift(newPet);
      editingPetId.value = newPet._uid;
    };

    const editPet = (pet) => startEdit(pet, editingPetId);
    const savePet = (pet) => saveItem(pet, 'pets', 'pet-', editingPetId);
    const cancelEditPet = (pet) => cancelEdit(pet, teamCats, editingPetId);
    const removePet = (pet) => removeItem(pet, 'pets', 'pet-', teamCats);

    // Image upload modal handlers
    const openImageUploadModal = (item, type) => {
      currentEditingItem.value = item;
      currentItemType.value = type;
      showImageUploadModal.value = true;
    };

    const handleImageUploadComplete = (data) => {
      if (currentEditingItem.value && data.imageUrl) {
        currentEditingItem.value.imageUrl = data.imageUrl;
      }
      showImageUploadModal.value = false;
      currentEditingItem.value = null;
      currentItemType.value = null;
    };

    const closeImageUploadModal = () => {
      showImageUploadModal.value = false;
      currentEditingItem.value = null;
      currentItemType.value = null;
    };

    // Load content from Firestore
    const loadContent = async () => {
      try {
        // Load about content
        const aboutDoc = await getDoc(doc(db, 'siteContent', 'about'));
        if (aboutDoc.exists()) {
          const data = aboutDoc.data();
          if (data.aboutParagraph) aboutParagraph.value = data.aboutParagraph;
          if (data.teamSectionTitle) teamSectionTitle.value = data.teamSectionTitle;
          if (data.teamSectionText) teamSectionText.value = data.teamSectionText;
          if (data.petsSectionTitle) petsSectionTitle.value = data.petsSectionTitle;
          if (data.petsSectionText) petsSectionText.value = data.petsSectionText;
        }

        // Load team members from Firestore and merge with defaults
        const teamSnapshot = await getDocs(collection(db, 'teamMembers'));
        if (!teamSnapshot.empty) {
          const firestoreMap = {};
          teamSnapshot.docs.forEach(d => {
            const data = d.data();
            firestoreMap[data.name] = {
              _uid: d.id,
              ...data,
              image: data.imageUrl || '',
              editing: false
            };
          });
          // Update defaults with Firestore data where names match
          teamMembers.value = teamMembers.value.map(member => {
            const saved = firestoreMap[member.name];
            if (saved) {
              // Use Firestore data but keep the local image if Firestore has none
              return { ...saved, image: saved.image || member.image };
            }
            return member;
          });
          // Add any Firestore members not in defaults
          Object.values(firestoreMap).forEach(saved => {
            if (!teamMembers.value.some(m => m.name === saved.name)) {
              teamMembers.value.push(saved);
            }
          });
        }

        // Load pets from Firestore and merge with defaults
        const petsSnapshot = await getDocs(collection(db, 'pets'));
        if (!petsSnapshot.empty) {
          const petsMap = {};
          petsSnapshot.docs.forEach(d => {
            const data = d.data();
            petsMap[data.name] = {
              _uid: d.id,
              ...data,
              image: data.imageUrl || '',
              editing: false
            };
          });
          teamCats.value = teamCats.value.map(pet => {
            const saved = petsMap[pet.name];
            if (saved) {
              return { ...saved, image: saved.image || pet.image };
            }
            return pet;
          });
          Object.values(petsMap).forEach(saved => {
            if (!teamCats.value.some(p => p.name === saved.name)) {
              teamCats.value.push(saved);
            }
          });
        }
      } catch (error) {
        console.error('Error loading content:', error);
      }
    };

    onMounted(() => {
      loadContent();
    });

    return {
      teamMembers,
      sortedTeamMembers,
      teamCats,
      openContactModal,
      openJoinModal,
      formatBio,
      toggleCard,
      toggleCatCard,
      flippedCards,
      flippedCatCards,
      isAdmin,
      aboutParagraph,
      teamSectionTitle,
      teamSectionText,
      petsSectionTitle,
      petsSectionText,
      editingAbout,
      editingTeamSection,
      editingPetsSection,
      editingMemberId,
      editingPetId,
      uploadingImage,
      startEditAbout,
      saveAbout,
      cancelEditAbout,
      startEditTeamSection,
      saveTeamSection,
      cancelEditTeamSection,
      startEditPetsSection,
      savePetsSection,
      cancelEditPetsSection,
      addTeamMember,
      editTeamMember,
      saveTeamMember,
      cancelEditTeamMember,
      removeTeamMember,
      addPet,
      editPet,
      savePet,
      cancelEditPet,
      removePet,
      showImageUploadModal,
      currentEditingItem,
      openImageUploadModal,
      handleImageUploadComplete,
      closeImageUploadModal
    };
  },
};
</script>

<style scoped>
.about-us-container {
  width: 100%;
  max-width: var(--maxContentWidth);
  margin: 0 auto;
  box-sizing: border-box;
  padding: var(--paddingSpaceous);
  min-height: calc(100vh - var(--navbarHeight));
  margin-top: var(--navbarHeight);
}

.header-container {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: var(--paddingSpaceous);
  padding: var(--paddingSpaceous);
  text-align: center;
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  margin-bottom: var(--paddingSpaceous);
}

.text-section {
  position: relative;
  z-index: 1;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.about-title {
  margin: 0 0 var(--paddingSpaceous) 0;
  font-size: var(--mainTitle);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  line-height: 0.9;
  text-align: center;
}

.about-title .subtitle-line {
  font-style: italic;
  font-size: var(--subTitle);
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.about-par {
  margin: 0 0 var(--paddingSpaceous) 0;
  font-size: var(--titleSubText);
  line-height: 1.7;
  color: var(--textColor);
  text-align: left;
  max-width: 50rem;
}

.whole-thing {
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  margin: 0 auto;
  position: relative;
}


.public-section {
  margin-top: var(--paddingSpaceous);
}

.pet-section {
  margin-top: var(--paddingSpaceous);
  margin-bottom: var(--paddingSpaceous);
}

.team-title {
  font-size: var(--subTitle);
  color: var(--textColor);
  margin-bottom: var(--paddingSpaceous);
  text-align: center;
  font-weight: var(--font-weight-bold);
}

.pet-section .team-title {
  font-size: var(--subTitle);
  color: var(--textColor);
  margin-bottom: var(--paddingSpaceous);
  text-align: center;
  font-weight: var(--font-weight-bold);
}

.team-text {
  font-size: var(--mainSubText);
  text-align: left;
  margin-bottom: var(--paddingSpaceous);
  color: var(--textColor);
  font-weight: 400;
  line-height: 1.7;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.pet-section .team-text {
  font-size: var(--mainSubText);
  text-align: left;
  margin-bottom: var(--paddingSpaceous);
  color: var(--textColor);
  font-weight: 400;
  line-height: 1.7;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.team-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 30px;
  margin-top: 40px;
  padding: 20px;
  list-style: none;
  margin-bottom: 50px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.pet-section .team-list {
  gap: 30px;
  align-items: flex-start;
}

.pet-section .team-member-container {
  margin: 0;
}

.team-member-container {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}

.card {
  position: relative;
  width: 280px;
  height: 400px;
  border-radius: var(--stdRadius);
  cursor: pointer;
  background-size: cover;
  background-position: center center;
  transition: all 0.3s ease;
  padding: 3px;
  background: #FBF9FB;
}

.card-inner {
  width: 100%;
  height: 100%;
  border-radius: calc(var(--stdRadius) - 3px);
  background-color: #ffffff;
  background-size: cover;
  background-position: center center;
  background-image: inherit;
  overflow: hidden;
  position: relative;
}

.public-section .team-member-container .card {
  width: 280px !important;
  height: 400px !important;
}

.pet-section .team-member-container:nth-child(1) .card {
  width: 400px;
  height: 460px;
  border-radius: var(--stdRadius);
}

.pet-section .team-member-container:nth-child(2) .card {
  width: 440px;
  height: 420px;
  border-radius: var(--stdRadius);
}

.pet-section .team-member-container:nth-child(3) .card {
  width: 280px;
  height: 520px;
  border-radius: var(--stdRadius);
}

.pet-section .team-member-container:nth-child(4) .card {
  width: 280px;
  height: 450px;
  border-radius: var(--stdRadius);
}

.pet-section .team-member-container:nth-child(5) .card {
  width: 280px;
  height: 560px;
  border-radius: var(--stdRadius);
}

.pet-section .card-info {
  padding: 0.8rem 0.6rem;
  min-height: 70px;
  max-height: 85px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: inherit;
}

.pet-section .title {
  font-size: 1.2rem;
  margin: 0 0 0.3rem 0;
  font-weight: 700;
}

.pet-section .subtitle {
  font-size: 0.9rem;
  font-style: italic;
  color: var(--textColor);
}

.card:hover {
  transform: translateY(-4px);
}

.card-front,
.card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  backface-visibility: hidden;
  overflow: hidden;
  transition: transform .5s cubic-bezier(0.785, 0.135, 0.150, 0.860);
}

.card-front {
  transform: translateX(0);
  z-index: 2;
}

.card-back {
  transform: translateX(120%);
  background: var(--bckColor);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  box-sizing: border-box;
  text-align: left;
  z-index: 1;
}

.card:hover .card-front {
  transform: translateX(-100%);
}

.card:hover .card-back {
  transform: translateX(0);
}

.card-info {
  color: var(--textColor);
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: var(--bckColor);
  padding: 1rem 0.8rem;
  text-align: center;
  transition: transform .5s cubic-bezier(0.785, 0.135, 0.150, 0.860);
  box-sizing: border-box;
  border-radius: 0 0 calc(var(--stdRadius) - 3px) calc(var(--stdRadius) - 3px);
}

.title {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--textColor);
}

.subtitle {
  color: var(--textColor);
  font-size: 1rem;
  font-weight: 400;
  margin: 0;
}

.card:hover .card-info {
  transform: translateY(100%);
}

.hover-content {
  color: var(--textColor);
  font-size: 0.95rem;
  line-height: 1.5;
  font-weight: 400;
  max-height: 100%;
  overflow-y: auto;
  padding: 1rem;
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.hover-content .art-highlight {
  color: var(--artColor);
  font-weight: 500;
}

.hover-content .science-highlight {
  color: var(--sciColor);
  font-weight: 500;
}

.button-row {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.contact-us-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: var(--bckColor);
  border: none;
  border-radius: var(--stdRadius);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
}

.contact-us-btn::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
}

.contact-us-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  z-index: 0;
}

.contact-us-btn span {
  position: relative;
  z-index: 2;
  color: var(--textColor);
}

.contact-us-btn svg {
  position: relative;
  z-index: 2;
  width: 16px;
  height: 16px;
}

.contact-us-btn svg path {
  stroke: var(--textColor);
}

.contact-us-btn:hover,
.contact-us-btn:focus {
  transform: translateY(-2px);
  outline: 2px solid var(--artColor);
  outline-offset: 2px;
}

/* Accessibility focus styles for cards */
.card:focus {
  outline: 3px solid var(--sciColor);
  outline-offset: 3px;
}

.card:focus-visible {
  outline: 3px solid var(--sciColor);
  outline-offset: 3px;
}

/* Flipped state for accessible card interactions */
.card[data-flipped="true"] .card-front {
  transform: translateX(-100%);
}

.card[data-flipped="true"] .card-back {
  transform: translateX(0);
}

@media (max-width: 768px) {
  .team-list {
    gap: 20px;
    padding: 15px;
  }
  
  .card {
    width: 260px !important;
    height: 360px !important;
  }
}

@media (max-width: 480px) {
  .team-list {
    gap: 15px;
    padding: 10px;
  }
  
  .card {
    width: 240px !important;
    height: 340px !important;
  }
}

.public-section .team-member-container .card:hover,
.pet-section .team-member-container .card:hover {
  transform: translateY(-4px);
}

.pet-section .team-member-container .card {
  transition: all 0.3s ease;
  transform: none;
}

.pet-section .team-member-container:nth-child(1) .card {
  background-position: center 20%;
  background-size: cover;
}

.pet-section .team-member-container:nth-child(2) .card {
  background-position: center top;
  background-size: cover;
}

.pet-section .team-member-container:nth-child(3) .card {
  background-position: center -25%;
  background-size: cover;
}

.pet-section .team-member-container:nth-child(4) .card {
  background-position: center center;
  background-size: cover;
}

.pet-section .team-member-container:nth-child(5) .card {
  background-position: center -25%;
  background-size: cover;
}

/* Admin editing styles */
.about-par-wrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.about-par-wrapper .about-par {
  flex: 1;
}

.section-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.edit-icon-btn {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.edit-icon-btn:hover {
  background: #d1d2e6;
  border-color: #d1d2e6;
}

.edit-icon-btn svg {
  stroke: #666;
}

.edit-icon-btn:hover svg {
  stroke: #333;
}

.edit-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: var(--stdRadius);
  padding: 1rem;
  margin-bottom: 1rem;
}

.edit-input {
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  box-sizing: border-box;
  font-family: inherit;
}

.edit-input:focus {
  outline: none;
  border-color: #d1d2e6;
}

.edit-textarea {
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  box-sizing: border-box;
  font-family: inherit;
  resize: vertical;
  line-height: 1.5;
}

.edit-textarea:focus {
  outline: none;
  border-color: #d1d2e6;
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.save-btn,
.cancel-btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.save-btn {
  background: white;
  border: 1px solid #d1d2e6;
  color: #333;
}

.save-btn:hover {
  background: #d1d2e6;
}

.cancel-btn {
  background: white;
  border: 1px solid #e0e0e0;
  color: #666;
}

.cancel-btn:hover {
  background: #f5f5f5;
}

.admin-actions {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: white;
  border: 1px solid #d1d2e6;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-btn:hover {
  background: #d1d2e6;
}

.add-btn svg {
  stroke: #666;
}

.add-btn:hover svg {
  stroke: #333;
}

.card-wrapper {
  position: relative;
}

.card-admin-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  z-index: 10;
}

.card-edit-btn,
.card-delete-btn {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.card-edit-btn:hover {
  background: #d1d2e6;
  border-color: #d1d2e6;
}

.card-delete-btn:hover {
  background: #ffebee;
  border-color: #ef5350;
}

.card-delete-btn:hover svg {
  stroke: #ef5350;
}

.member-edit-form {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: var(--stdRadius);
  padding: 1rem;
  width: 280px;
}

.image-preview {
  margin-bottom: 0.5rem;
  border-radius: 6px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.image-upload-section {
  margin-bottom: 0.5rem;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d2e6;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  background: #d1d2e6;
}

.upload-btn svg {
  stroke: #666;
}

.upload-btn:hover svg {
  stroke: #333;
}

</style>